import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatBRL } from "../lib/api";
import { Flame, Trophy, Target, Sparkles, Rocket, LogOut, Zap, TrendingUp, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar } from "../components/Avatar";
import AvatarPickerSheet from "../components/AvatarPickerSheet";

export default function Dashboard({ onNewSale }) {
  const { user, levelInfo, logout, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [missions, setMissions] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [showAvatar, setShowAvatar] = useState(false);
  const isObserver = user?.role === "observer";

  const loadAll = async () => {
    try {
      const [s, m, r] = await Promise.all([
        api.get("/stats/me"),
        api.get("/missions"),
        api.get("/ranking"),
      ]);
      setStats(s.data);
      setMissions(m.data);
      setRanking(r.data);
    } catch {}
    await refresh();
  };

  useEffect(() => {
    loadAll();
     
  }, []);

  const myRank = ranking.find((r) => r.id === user?.id);
  const dailyMissions = missions.filter((m) => m.scope === "daily").slice(0, 2);

  return (
    <div className="arcade-grid min-h-screen pb-28">
      <div className="phone-frame phone-frame-full px-4 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAvatar(true)}
              data-testid="dashboard-avatar-btn"
              className="relative group"
            >
              <Avatar user={user} size={44} />
              <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-fuchsia-500 border-2 border-slate-900 flex items-center justify-center">
                <Pencil size={9} className="text-white" strokeWidth={3} />
              </span>
            </button>
            <div>
              <p className="text-xs font-mono text-slate-400 uppercase tracking-widest">Olá, campeão</p>
              <h1 className="font-arcade text-xl font-bold text-white leading-tight" data-testid="dashboard-user-name">
                {user?.name}
              </h1>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-10 h-10 rounded-xl bg-slate-800/70 border border-slate-700 flex items-center justify-center text-slate-300"
          >
            <LogOut size={18} />
          </button>
        </div>

        {/* Level card */}
        <div className="neon-card-violet p-5 mb-4 slide-up relative overflow-hidden" data-testid="seller-level-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="chip chip-pink mb-2">⚡ Nível atual</p>
              <div className="flex items-baseline gap-2">
                <span className="font-arcade text-5xl font-extrabold text-white">
                  {levelInfo?.level || 1}
                </span>
                <span className="font-mono text-sm text-fuchsia-300" data-testid="seller-xp-counter">
                  {levelInfo?.xp || 0} XP
                </span>
              </div>
            </div>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center badge-pulse shadow-xl shadow-amber-500/30">
              <Trophy size={28} className="text-white" strokeWidth={2.4} />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-xs font-mono text-slate-300 mb-1.5">
              <span>Progresso para nível {(levelInfo?.level || 1) + 1}</span>
              <span>{levelInfo?.progress_in_level || 0}/{levelInfo?.xp_to_next_level || 500}</span>
            </div>
            <div className="progress-track" data-testid="level-progress-bar">
              <div className="progress-fill" style={{ width: `${levelInfo?.progress_pct || 0}%` }} />
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="neon-card-cyan p-4" data-testid="stat-today-value">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={14} className="text-cyan-300" />
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">Hoje</span>
            </div>
            <p className="font-arcade text-xl font-bold text-white">{formatBRL(stats?.today_value)}</p>
            <p className="text-xs text-cyan-300 mt-0.5">{stats?.today_count || 0} vendas</p>
          </div>
          <div className="neon-card-gold p-4" data-testid="stat-total-value">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-amber-300" />
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">Total</span>
            </div>
            <p className="font-arcade text-xl font-bold text-white">{formatBRL(stats?.total_value)}</p>
            <p className="text-xs text-amber-300 mt-0.5">{stats?.sales_count || 0} vendas</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="neon-card p-4 flex items-center gap-3" data-testid="stat-streak">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Flame size={22} className="text-white" strokeWidth={2.6} />
            </div>
            <div>
              <p className="font-arcade text-2xl font-bold text-white leading-none">{stats?.streak_days || 0}</p>
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mt-1">Dias em chamas</p>
            </div>
          </div>
          <Link to="/ranking" className="neon-card p-4 flex items-center gap-3" data-testid="stat-rank">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-violet-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
              <Trophy size={22} className="text-white" strokeWidth={2.6} />
            </div>
            <div>
              <p className="font-arcade text-2xl font-bold text-white leading-none">
                #{myRank?.rank || "-"}
              </p>
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mt-1">Posição</p>
            </div>
          </Link>
        </div>

        {/* Big CTA */}
        {!isObserver && (
          <button
            onClick={onNewSale}
            data-testid="quick-sale-trigger-btn"
            className="btn-arcade w-full flex items-center justify-center gap-2 text-lg mb-6"
          >
            <Rocket size={22} />
            Registrar nova venda
          </button>
        )}
        {isObserver && (
          <div className="neon-card p-4 mb-6 flex items-center gap-3" data-testid="observer-banner">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shrink-0">
              <span className="text-white text-xl">👁️</span>
            </div>
            <div>
              <p className="font-arcade text-sm font-bold text-white">Modo Observador</p>
              <p className="text-[11px] text-slate-400">Você acompanha o time em tempo real. Vendas e resgates ficam com os vendedores.</p>
            </div>
          </div>
        )}

        {/* Daily missions preview */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-arcade text-lg font-bold text-white flex items-center gap-2">
            <Target size={18} className="text-fuchsia-400" />
            Missões de hoje
          </h2>
          <Link to="/missoes" className="text-xs font-mono text-fuchsia-300 uppercase tracking-widest">
            Ver todas →
          </Link>
        </div>

        <div className="space-y-3 mb-6">
          {dailyMissions.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">Sem missões ativas</p>
          )}
          {dailyMissions.map((m) => {
            const pct = Math.min(100, (m.progress / m.target) * 100);
            return (
              <div key={m.id} className="neon-card p-4" data-testid="mission-item-daily">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-arcade text-sm font-bold text-white">{m.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{m.description}</p>
                  </div>
                  <span className="chip chip-gold whitespace-nowrap">+{m.xp_reward} XP</span>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-[11px] font-mono text-slate-400 mb-1">
                    <span>
                      {m.target_type === "sales_value" ? formatBRL(m.progress) : m.progress}
                    </span>
                    <span>
                      {m.target_type === "sales_value" ? formatBRL(m.target) : m.target}
                    </span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <AvatarPickerSheet open={showAvatar} onClose={() => setShowAvatar(false)} />
    </div>
  );
}
